import type { EventVisibility } from '../../api/ownerTypes'
import { presetColors } from '../format'

/**
 * "Wie ziet welke foto's" — de twee keuzevakken. Komt terug in stap 3 van de
 * wizard en in Instellingen, mobiel onder elkaar en op desktop naast elkaar.
 */
export function VisibilityChoice({
  value,
  onChange,
}: {
  value: EventVisibility
  onChange: (value: EventVisibility) => void
}) {
  const options: { id: EventVisibility; title: string; detail: string }[] = [
    {
      id: 'Shared',
      title: 'Alle gasten zien alles',
      detail: 'Het feest wordt één gedeeld album.',
    },
    {
      id: 'OwnPhotosOnly',
      title: 'Iedereen ziet enkel eigen foto’s',
      detail: 'Jullie zien als eigenaar wel alles.',
    },
  ]

  return (
    <div
      role="radiogroup"
      aria-label="Wie ziet welke foto’s"
      className="flex flex-col gap-2.5 lg:flex-row lg:gap-3.5"
    >
      {options.map((option) => {
        const selected = option.id === value

        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.id)}
            className={
              'rounded-notice bg-surface lg:rounded-card flex flex-1 items-start gap-3.25 p-4 text-left transition-colors lg:gap-3.5 lg:p-5 ' +
              (selected
                ? 'border-accent border-[1.5px]'
                : 'border-line-card hover:border-line-warm hover:bg-notice border')
            }
          >
            <span
              aria-hidden="true"
              className={
                'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] lg:size-5.5 lg:text-xs ' +
                (selected ? 'bg-accent text-accent-ink' : 'border-line border-[1.5px]')
              }
            >
              {selected ? '✓' : ''}
            </span>

            <span>
              <span className="block text-[15px] font-semibold lg:text-base">{option.title}</span>
              <span className="text-ink-soft mt-1 block text-[13px] leading-[1.5] lg:mt-1.5 lg:text-sm">
                {option.detail}
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * Staat de galerij open voor gasten, of is hij privé.
 *
 * Twee kaarten en geen aan/uit-schuifje, in dezelfde vorm als de keuze erboven.
 * Een schuifje laat je raden wat de andere stand doet, en dat is precies wat er
 * hiervoor misging: "afsluiten" zei niet wat er dicht ging. Hier staat allebei
 * de gevolgen uitgeschreven, naast elkaar, voor je kiest.
 *
 * De eigenaar wordt in beide standen genoemd. Dat hij er zelf altijd in kan is
 * de vraag die je je stelt op het moment dat je overweegt hem dicht te zetten.
 */
export function PrivateGalleryChoice({
  value,
  onChange,
}: {
  value: boolean
  onChange: (value: boolean) => void
}) {
  const options: { id: boolean; title: string; detail: string }[] = [
    {
      id: false,
      title: 'Gasten kunnen erbij',
      detail: 'Met de QR-code komen ze in de galerij en kunnen ze foto’s toevoegen.',
    },
    {
      id: true,
      title: 'Privé — alleen jij',
      detail: 'De QR-code werkt niet meer: niemand kijkt nog mee, niemand voegt nog iets toe.',
    },
  ]

  return (
    <div
      role="radiogroup"
      aria-label="Toegang tot de galerij"
      className="flex flex-col gap-2.5 lg:flex-row lg:gap-3.5"
    >
      {options.map((option) => {
        const selected = option.id === value

        return (
          <button
            key={String(option.id)}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.id)}
            className={
              'rounded-notice bg-surface lg:rounded-card flex flex-1 cursor-pointer items-start gap-3.25 p-4 text-left transition-colors lg:gap-3.5 lg:p-5 ' +
              (selected
                ? 'border-accent border-[1.5px]'
                : 'border-line-card hover:border-line-warm hover:bg-notice border')
            }
          >
            <span
              aria-hidden="true"
              className={
                'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] lg:size-5.5 lg:text-xs ' +
                (selected ? 'bg-accent text-accent-ink' : 'border-line border-[1.5px]')
              }
            >
              {selected ? '✓' : ''}
            </span>

            <span>
              <span className="block text-[15px] font-semibold lg:text-base">{option.title}</span>
              <span className="text-ink-soft mt-1 block text-[13px] leading-[1.5] lg:mt-1.5 lg:text-sm">
                {option.detail}
              </span>
            </span>
          </button>
        )
      })}
    </div>
  )
}

/**
 * "Kleur van het event" — vier voorkeuzes en een eigen hex. De gekozen kleur
 * krijgt een ring in zijn eigen kleur, precies zoals in de bundle.
 */
export function AccentChoice({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  const normalized = value.toUpperCase()

  return (
    <div className="rounded-notice border-line-card bg-surface lg:rounded-card border p-4 lg:p-5">
      <div className="flex gap-3">
        {presetColors.map((color) => (
          <button
            key={color}
            type="button"
            aria-label={`Kleur ${color}`}
            aria-pressed={color === normalized}
            onClick={() => onChange(color)}
            style={{
              background: color,
              boxShadow:
                color === normalized
                  ? `0 0 0 2px var(--color-surface), 0 0 0 4px ${color}`
                  : undefined,
            }}
            className="h-13 flex-1 rounded-xl lg:h-15"
          />
        ))}
      </div>

      <div className="border-line-menu mt-4 flex items-center gap-3 border-t pt-4">
        <span
          aria-hidden="true"
          style={{ background: normalized }}
          className="size-10.5 shrink-0 rounded-[10px] lg:size-11.5 lg:rounded-xl"
        />

        <div className="flex-1">
          <label
            htmlFor="accent-hex"
            className="text-ink-faint mb-1.5 block text-xs tracking-[0.14em] uppercase"
          >
            Eigen kleur
          </label>

          {/* Een echte kleurkiezer achter het hex-veld: typen mag, prikken ook. */}
          <div className="border-line-card flex items-center gap-2 rounded-[10px] border px-3 py-2.5">
            <input
              id="accent-hex"
              value={normalized}
              onChange={(event) => onChange(event.target.value.toUpperCase())}
              maxLength={7}
              spellCheck={false}
              className="w-full bg-transparent font-mono text-[15px] outline-none"
            />
            <input
              type="color"
              aria-label="Kleur kiezen"
              value={/^#[0-9A-F]{6}$/.test(normalized) ? normalized : '#A0552F'}
              onChange={(event) => onChange(event.target.value.toUpperCase())}
              className="size-5 shrink-0 cursor-pointer border-0 bg-transparent p-0"
            />
          </div>
        </div>
      </div>

      <p className="text-ink-soft mt-3.5 text-[13px] leading-[1.5] lg:text-sm">
        Bepaalt de knoppen en accenten in het gastenportaal.
      </p>
    </div>
  )
}
